import { Injectable } from '@angular/core';

import { Subject } from 'rxjs';

/**
 * @typedef UserMode
 * @description Represents the different access modes available to a user.
 * - GUEST: Guest access (anonymous, via QR code)
 * - LOGIN: Login access (via username and password)
 */
export type UserMode = 'GUEST' | 'LOGIN';

/**
 * @typedef WorkMode
 * @description Which application / device role is active.
 * - POS: main TPV (`app.<domain>`)
 * - PTF: Pinpad payment-terminal app (`ptf.<domain>`)
 * - QRA: guest QR access app (`qra.<domain>`)
 */
export type WorkMode = 'POS' | 'PTF' | 'QRA';

/**
 * @typedef UserSession
 * @description Represents the user's session information.
 * @property {string | null} session - The unique identifier for the user's session, or `null` if no session is active.
 * @property {string | null} device - The unique identifier for the user's device, or `null` if no device is set.
 */
export type UserSession = {
    session: string | null,
    device: string | null
};

/************************************/
/* STATE SERVICE                     */
/************************************/

/**
 * @class stateService
 * @description Service for managing the user's access mode, work mode,
 * and session expiration status. It provides mechanisms to set and get the
 * current user mode and notify when the session has expired.
 */
@Injectable({
    providedIn: 'root'
})
export class stateService {

    /******************************/
    /* ACCESS MODE                */
    /******************************/

    private _access: UserMode | null = null;

    /**
     * @description Gets or sets the current user access mode.
     * @type {UserMode | null}
     */    
    get Access(): UserMode | null {
        return this._access
    }

    set Access(value: UserMode){
        this._access = value;
    }

    /******************************/
    /* WORK MODE                  */
    /******************************/

    private _workMode: WorkMode = 'POS';

    /**
     * @description Application work mode (`POS` by default; `PTF` / `QRA` set by their app roots).
     * Distinct from {@link Access}: e.g. QRA uses `Access = 'GUEST'` and `WorkMode = 'QRA'`.
     * Set once by the app root; shared libs read it as the single source of truth.
     */
    get WorkMode(): WorkMode {
        return this._workMode;
    }

    set WorkMode(value: WorkMode) {
        this._workMode = value;
    }

    /******************************/
    /* SESSION CONTROL            */
    /******************************/

    private _onDevice = new Subject<void>();
    private _onSession = new Subject<void>();

    /**
     * @description Observable that emits when the device ID is updated in the user session.
     * @type {Observable<void>}
     */    
    public OnDevice = this._onDevice.asObservable();
        
    /**
     * @description Observable that emits when the session ID is updated in the user session.
     * @type {Observable<void>}
     */
    public OnSession = this._onSession.asObservable();

    private _userSession: UserSession = {
        device: null,
        session: null
    };

    /**
     * @description Gets or sets the current user session information.
     * @type {UserSession | null}
     */    
    get userSession() : UserSession | null {
        return this._userSession;
    }

    /**
     * @description Retrieves the session ID from the current user session.
     * @returns {string | null} The session ID, or `null` if no session is set.
     */    
    get session(): string | null {
        if (this._userSession){
            return encodeURIComponent(this._userSession.session ?? '');
        }
        return null;
    }

    /**
     * @description Sets a new session ID. Triggers the session update event if the value changes.
     * @param {string} value - The new session ID.
     */    
    set session(value: string | null){
        if (this.session != value){
            this._userSession.session = value;
            this._onSession.next();
        }
    }

    /**
     * @description Retrieves the device ID from the current user session.
     * @returns {string | null} The device ID, or `null` if no session is set.
     */    
    get device(): string | null {
        if (this._userSession){
            return encodeURIComponent(this._userSession.device ?? '');
        }
        return null;
    }

    /**
     * @description Sets a new device ID. Triggers the device update event if the value changes.
     * @param {string} value - The new device ID.
     */    
    set device(value: string){
        if (this.device != value){
            this._userSession.device = value;
            this._onDevice.next();
        }
    }

    /******************************/
    /* PLACE (for URL params)     */
    /******************************/

    private _place: string | null = null;

    /**
     * @description Retrieves the current place objid (chosen after login or from QR scan). Used by adhocService for URL parameters.
     * @returns {string | null} The place objid, or `null` if no place is set.
     */
    get place(): string | null {
        return encodeURIComponent(this._place ?? '');
    }

    /**
     * @description Sets the current place objid.
     * @param {string | null} value - The place objid, or `null` to clear.
     */
    set place(value: string | null){
        this._place = value ?? null;
    }

    /*********************************/
    /* SESSION EXPIRATION EVENT      */
    /*********************************/

    private _onExpired = new Subject<void>();

    /**
     * @description Observable that emits when the session expires, allowing subscribers to react to expiration events.
     * @type {Observable<void>}
     */    
    public OnExpired = this._onExpired.asObservable();

    private _isexpired = false;

    /**
     * @description Gets or sets the expiration status of the session. 
     * If set to `true`, it emits an expiration event.
     * @type {boolean}
     */    
    get IsExpired(): boolean{
        return this._isexpired;
    }

    set IsExpired(value: boolean){
        if (this._isexpired !== value){
            if (value){
                this._onExpired.next();
            }
            
            this._isexpired = value;
        }
    }

    /*********************************/
    /* LOADING READY EVENT           */
    /*********************************/

    private _onReady = new Subject<void>();

    /**
     * @description Observable that emits when the system is ready, allowing subscribers to react when everything is loaded.
     * @type {Observable<void>}
     */    
    public OnReady = this._onReady.asObservable();

    private _isready = false;

    /**
     * @description Gets or sets the ready status of the session. 
     * If set to `true`, it emits a ready event.
     * @type {boolean}
     */    
    get IsReady(): boolean{
        return this._isready;
    }

    set IsReady(value: boolean){
        if (this._isready !== value){
            if (value){
                this._onReady.next();
            }
            
            this._isready = value;
        }
    }
}